import { API_ENDPOINT } from '../services/api';
import { getScaledValue } from '../ui/styled';
import branding from '../../config/branding';
import { dedupeByDocumentId, resolveCardImage, resolveContentType } from './card';

const profilesTitles: Record<string, string> = {
  male: 'Masculino',
  female: 'Feminino',
};

/** Raw Strapi entity — may be v4 (nested `attributes`) or v5 (flat). */
type StrapiRaw = Record<string, unknown>;

// v4 nests fields under `attributes`; v5 is flat on the entity. Return whichever holds the fields.
const getAttr = (item: StrapiRaw | undefined): StrapiRaw =>
  (item?.attributes as StrapiRaw) || item || {};

// A single relation/media is `{ data: { id, attributes } }` in v4 and a direct object in v5.
const getData = (item: StrapiRaw | undefined): StrapiRaw => {
  if (!item?.data) return item || {};
  const data = item.data as StrapiRaw;
  const attrs = data.attributes as StrapiRaw | undefined;
  return attrs ? { id: data.id, ...attrs } : data;
};

export const parseStrapiImage = (url: string | null | undefined) => {
  return url
    ? url?.indexOf('://') !== -1
      ? url
      : url?.indexOf(branding.cdnDomain) !== -1
        ? `https://${url}`
        : `${API_ENDPOINT.replace(/\/api$/, '')}${url}`
    : '';
};

export const parseProfile = (item: Record<string, unknown>) => {
  return {
    ...item,
    id: item.id,
    name: item.name,
    image: { uri: parseStrapiImage((item?.picture as Record<string, string>)?.url) },
    gender: { title: profilesTitles[item.gender as string], type: item.gender },
  };
};

export const parseProfileImage = (item: Record<string, unknown>) => {
  return {
    id: item.id,
    name: item.name,
    image: { uri: parseStrapiImage(item.url as string) },
  };
};

const categoriesOption: Record<string, { title: string; color: string }> = {
  Filme: {
    title: 'Filme',
    color: 'blue',
  },
  Série: {
    title: 'Série',
    color: 'yellow',
  },

  Musical: {
    title: 'Musical',
    color: 'blue',
  },
  Educativo: {
    title: 'Educativo',
    color: 'green',
  },
};

export const parseBanner = (item: Record<string, unknown>) => {
  const show = getData(item?.featured_show as StrapiRaw);
  const showAttr = getAttr(show);
  const banner = getData(item?.featured_banner as StrapiRaw);
  const bannerAttr = getAttr(banner);
  return {
    id: showAttr?.id,
    // v5 findOne routes 404 on numeric ids — navigation needs documentId.
    documentId: (show?.documentId ?? showAttr?.documentId) as string | undefined,
    title: showAttr?.name,
    description: showAttr?.description,
    image: {
      uri: parseStrapiImage(
        (bannerAttr?.url as string | undefined) || (showAttr?.bunny_image as string | undefined),
      ),
    },
    category: categoriesOption['Filme'],
  };
};

interface IconDef {
  icon: string;
  width: number;
  height: number;
  fill: string;
}

const categoryIcons: Record<string, IconDef> = {
  Playlist: { icon: 'play', width: getScaledValue(9), height: getScaledValue(11), fill: '#ffffff' },
  Conteúdos: {
    icon: 'smile',
    width: getScaledValue(15),
    height: getScaledValue(17),
    fill: branding.colors.primary,
  },
  Educativos: {
    icon: 'paint',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: branding.colors.primary,
  },
  Filmes: {
    icon: 'popcorn',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: branding.colors.primary,
  },
  'Ver Mais': {
    icon: 'plus',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: '#ffffff',
  },
};

export const parseCategory = (item: Record<string, unknown>) => {
  const attrs = getAttr(item as StrapiRaw);
  const icon = getData(attrs?.icon as StrapiRaw);
  const iconAttr = getAttr(icon);
  return {
    title: attrs?.name,
    id: item?.id,
    icon: categoryIcons[attrs?.name as string] || {},
    image: iconAttr?.url ? parseStrapiImage(iconAttr.url as string) : null,
  };
};

const collectionResources: Record<string, Record<string, unknown>> = {
  'Meus Favoritos': {
    icon: {
      icon: 'liked',
      width: getScaledValue(22),
      height: getScaledValue(20),
      fill: branding.colors.primary,
    },
  },
  'As 20 Músicas + Vistas': {
    icon: {
      icon: 'music',
      width: getScaledValue(17),
      height: getScaledValue(21),
      fill: branding.colors.primary,
    },
  },
  'Cante e Dance': {
    icon: {
      icon: 'music',
      width: getScaledValue(17),
      height: getScaledValue(21),
      fill: branding.colors.primary,
    },
  },
};

export const parseCollection = (item: Record<string, unknown>) => {
  const attrs = getAttr(item as StrapiRaw);
  const icon = getData(attrs?.icon as StrapiRaw);
  const iconAttr = getAttr(icon);
  const streamShows = attrs?.stream_shows as Record<string, unknown> | undefined;
  const shows =
    (streamShows?.data as Record<string, unknown>[]) ||
    (attrs?.stream_shows as Record<string, unknown>[]) ||
    [];
  // `icon` comes from the static collectionResources map (present only for known
  // collection names); declared explicitly so consumers can read `.icon`.
  const resources = (collectionResources[attrs?.name as string] || {}) as {
    icon?: Record<string, unknown>;
  };
  return {
    id: item?.id,
    title: attrs?.name,
    icon: resources.icon,
    videos: Array.isArray(shows) ? dedupeByDocumentId(shows.map(parseVideo)) : [],
    image: iconAttr?.url ? parseStrapiImage(iconAttr.url as string) : null,
  };
};

export const parseVideo = (item: Record<string, unknown>) => {
  const attrs = item?.attributes as Record<string, unknown> | undefined;
  const previewUrl =
    (attrs?.bunny_preview as string | undefined) || (item?.bunny_preview as string | undefined);
  return {
    id: item?.id,
    // v5 findOne routes 404 on numeric ids — keep documentId for navigation.
    documentId: (item?.documentId ?? attrs?.documentId) as string | undefined,
    title: attrs?.name || item?.name,
    description: attrs?.description || item?.description,
    cover: { uri: parseStrapiImage(resolveCardImage(item)) },
    preview: previewUrl ? { uri: parseStrapiImage(previewUrl) } : undefined,
    contentType: resolveContentType(item),
  };
};

export const parseVideoShow = (item: Record<string, unknown>) => {
  const attrs = item?.attributes as Record<string, unknown> | undefined;
  const widePic = attrs?.wide_picture as Record<string, unknown> | undefined;
  const widePicData = widePic?.data as Record<string, unknown> | undefined;
  const widePicAttrs = widePicData?.attributes as Record<string, unknown> | undefined;
  const flatWidePic = item?.wide_picture as Record<string, unknown> | undefined;
  const flatWidePicData = flatWidePic?.data as Record<string, unknown> | undefined;
  const flatWidePicAttrs = flatWidePicData?.attributes as Record<string, unknown> | undefined;
  return {
    ...item,
    title: item.name,
    cover: {
      uri: parseStrapiImage(
        (widePicAttrs?.url as string) ||
          (flatWidePicAttrs?.url as string) ||
          (flatWidePic?.url as string) ||
          // v5 flat show: signed Bunny image lives directly on the entity.
          (attrs?.bunny_image as string) ||
          (item?.bunny_image as string),
      ),
    },
    thumb: { uri: parseStrapiImage(resolveCardImage(item)) },
  };
};
